import React from 'react';
import { 
  HiOutlineClock,
  HiOutlineExclamationCircle,
  HiOutlineXCircle,
  HiOutlineCheckCircle,
  HiRefresh
} from "react-icons/hi";

/**
 * Capitalizes a string, converting snake_case or space-separated words to Title Case
 */
export const capitalizeString = (str: string): string => {
  // Handle snake_case
  if (str.includes('_')) {
    return str.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Handle space-separated words
  if (str.includes(' ')) {
    return str.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Standard capitalization for single words
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * A common utility function to format status values consistently across the application
 */
export const formatStatusForDisplay = (status: string): string => {
  // Special cases for each status to ensure consistent formatting
  switch(status.toLowerCase()) {
    case "on_track":
    case "on track":
      return "On Track";
    case "delayed":
      return "Delayed";
    case "completed":
      return "Completed";
    case "blocked":
      return "Blocked";
    case "unallocated":
      return "Unallocated";
    case "cache_in_progress":
      return "Data Collecting";
    case "not_started":
    case "not started":
      return "Not Started";
    default:
      // Use the capitalizeString function for any custom status
      return capitalizeString(status);
  }
};

/**
 * Gets the appropriate color class for a status badge
 */
export const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "on_track":
    case "on track":
      return "!bg-emerald-500 dark:!bg-emerald-500 !text-white";
    case "delayed":
      return "!bg-amber-500 dark:!bg-amber-500 !text-white";
    case "blocked":
      return "!bg-rose-500 dark:!bg-rose-500 !text-white";
    case "unallocated":
      return "!bg-red-500 dark:!bg-red-500 !text-white";
    case "completed":
      return "!bg-blue-500 dark:!bg-blue-500 !text-white";
    case "cache_in_progress":
      return "!bg-purple-500 dark:!bg-purple-500 !text-white"; 
    case "not_started":
    case "not started":
      return "!bg-gray-500 dark:!bg-gray-500 !text-white";

    default:
      return "!bg-slate-500 dark:!bg-slate-500 !text-white";
  }
};

/**
 * Gets the appropriate border color class for a status
 */
export const getStatusBorderColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "on_track":
    case "on track":
      return "border-l-4 border-emerald-500";
    case "delayed":
      return "border-l-4 border-amber-500";
    case "blocked":
      return "border-l-4 border-rose-500";
    case "completed":
      return "border-l-4 border-blue-500";
    case "cache_in_progress":
      return "border-l-4 border-purple-500";
    case "not_started":
    case "unallocated":
      return "border-l-4 border-red-500";
    case "not started":
      return "border-l-4 border-gray-500";
    default:
      return "border-l-4 border-slate-500";
  }
};

/**
 * Gets the appropriate color for progress bars based on percentage
 */
export const getProgressColor = (percentage: number) => {
  if (percentage >= 100) return "bg-blue-500";
  if (percentage >= 70) return "bg-emerald-500";
  if (percentage >= 40) return "bg-amber-500";
  return "bg-gray-300";
};

/**
 * Gets an icon component based on the status
 */
const getStatusIcon = (status: string): React.ReactNode => {
  // Convert the status to lowercase for case-insensitive comparison
  const statusLower = status.toLowerCase();
  
  switch (statusLower) {
    case "on_track":
    case "on track":
      return <HiOutlineClock className="h-3.5 w-3.5" />;
    case "delayed":
      return <HiOutlineExclamationCircle className="h-3.5 w-3.5" />;
    case "unallocated":
      return <HiOutlineExclamationCircle className="h-3.5 w-3.5" />;
    case "blocked":
      return <HiOutlineXCircle className="h-3.5 w-3.5" />;
    case "completed":
      return <HiOutlineCheckCircle className="h-3.5 w-3.5" />;
    case "cache_in_progress":
      return <HiRefresh className="h-3.5 w-3.5" />;
    case "not_started":
    case "not started":
      return <HiOutlineClock className="h-3.5 w-3.5 text-gray-500" />;
    default:
      return <HiOutlineClock className="h-3.5 w-3.5" />;
  }
};

/**
 * Props for the StatusDisplay component
 */
interface StatusDisplayProps {
  status: string;
  showIcon?: boolean;
  showText?: boolean;
  className?: string;
}

/**
 * A component to display the status with icon and text
 */
const StatusDisplay: React.FC<StatusDisplayProps> = ({
  status,
  showIcon = true,
  showText = true,
  className = ""
}) => {
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {showIcon && getStatusIcon(status)}
      {showText && <span>{formatStatusForDisplay(status)}</span>}
    </div>
  );
};

export { getStatusIcon, StatusDisplay };
export default StatusDisplay; 